import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from 'components/Modal/Modal';
import Textarea from 'components/Textarea/Textarea';
import TextField from 'components/TextField/TextField';
import { REASON } from 'constants/formFields';
import { DESCRIPTION_LENGTH } from 'constants/general';
import { deactivationSchema } from 'utils/validators';

import type { TDeactivationSchema } from 'utils/validators';
import type IDeactivationModal from './interfaces/IDeactivationModal';

function DeactivationModal({
  open,
  title,
  text,
  onReject,
  onSubmit,
  isLoading,
  placeholder,
  hideLabel,
  showInfoIcon,
  offValidation,
  textFieldType,
  fieldText,
  confirmTitle,
  titleLabel,
  className,
  titleClassName,
  disabledConfirmBtn,
}: IDeactivationModal) {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    reset,
    watch,
  } = useForm<TDeactivationSchema>({
    mode: 'onSubmit',
    resolver: offValidation ? undefined : yupResolver(deactivationSchema),
    defaultValues: {
      [REASON]: fieldText || '',
    },
  });

  useEffect(() => {
    reset({
      [REASON]: fieldText,
    });
  }, [fieldText, reset, open]);

  const onHandleConfirm = handleSubmit((values) => {
    onSubmit(values.reason);
  });

  const handleCancel = () => {
    onReject();
    reset({
      [REASON]: '',
    });
  };

  const titleField = titleLabel || t('common.reason');
  const disabledConfirm = disabledConfirmBtn ? !watch(REASON) || watch(REASON)?.length < 3 : false;

  return (
    <Modal
      open={open}
      title={title || t('common.deactivationReason')}
      description={text}
      closeTitle={t('common.cancel')}
      onReject={handleCancel}
      onConfirm={onHandleConfirm}
      isLoading={isLoading}
      showInfoIcon={showInfoIcon}
      confirmTitle={confirmTitle}
      className={className}
      titleClassName={titleClassName}
      disabledConfirm={disabledConfirm}
    >
      <Controller
        control={control}
        name={REASON}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          textFieldType ? (
            <TextField
              name={name}
              value={value}
              label={hideLabel ? '' : titleField}
              placeholder={placeholder || t('common.typeYourMessage')}
              onChange={(e) => onChange(e.target.value.slice(0, DESCRIPTION_LENGTH))}
              error={error?.message}
            />
          ) : (
            <Textarea
              name={name}
              value={value}
              label={hideLabel ? '' : titleField}
              placeholder={placeholder || t('common.typeYourMessage')}
              onChange={(e) => onChange(e.target.value.slice(0, DESCRIPTION_LENGTH))}
              error={error?.message}
              charactersLimit={DESCRIPTION_LENGTH}
            />
          )
        )}
      />
    </Modal>
  );
}

export default DeactivationModal;
